// Create Match - the form model (pure, no React). Same rules and payload as the old Create Match
// modal in Matches.js: POST /api/matches receives exactly what it received before.

export const emptyMatchForm = () => ({
  team1_id: '', team2_id: '', server_id: '', best_of: 1,
  maps: [''], map_sides: ['team1_ct'], map_knife: [false], has_knife_round: false,
  admin_only_start: true, enforce_teams: false, allow_stop: false, skip_backup_load: false,
  custom_avatars: false, random_password: false, steam_login_required: false,
  chat_prefix: '', chat_prefix_color: '', cvars_warmup: [], cvars_knife_live: [],
  veto_enabled: false, veto_style: '', veto_use_coin_flip: false, veto_discord_webhook: '',
  show_killfeed: true, stream_delay: 0, cstv1_public: false, cstv2_public: false, tech_budget_seconds: 0,
});

export const POOLS = [
  { value: 'active_duty', label: 'Active Duty' },
  { value: 'wingman', label: 'Wingman' },
  { value: 'competitive', label: 'Competitive' },
];

const pad = (arr, n, fill) => { const a = [...(arr || [])]; while (a.length < n) a.push(fill); return a; };

/** Keep the per-map arrays at least best_of long (extra entries are ignored on submit) */
export function withBestOf(form, bo) {
  return { ...form, best_of: bo, maps: pad(form.maps, bo, ''), map_sides: pad(form.map_sides, bo, 'team1_ct'), map_knife: pad(form.map_knife, bo, false) };
}

/** Season preset → form (the old loadSeason) */
export function applySeason(form, s) {
  const parse = (v) => (v ? (typeof v === 'string' ? JSON.parse(v) : v) : []);
  return {
    ...withBestOf(form, s.best_of || 1),
    admin_only_start: !!s.admin_only_start,
    enforce_teams: !!s.enforce_teams,
    allow_stop: !!s.allow_stop,
    skip_backup_load: !!s.skip_backup_load,
    custom_avatars: !!s.custom_avatars,
    random_password: !!s.random_password,
    steam_login_required: !!s.steam_login_required,
    chat_prefix: s.chat_prefix || '',
    chat_prefix_color: s.chat_prefix_color || '',
    cvars_warmup: parse(s.cvars_warmup),
    cvars_knife_live: parse(s.cvars_knife_live),
    veto_enabled: !!s.veto_enabled,
    veto_style: s.veto_style || '',
    veto_use_coin_flip: !!s.veto_use_coin_flip,
    show_killfeed: (s.show_killfeed === undefined || s.show_killfeed === null) ? true : !!s.show_killfeed,
    stream_delay: s.stream_delay || 0,
    cstv1_public: !!s.cstv1_public,
    cstv2_public: !!s.cstv2_public,
    tech_budget_seconds: s.tech_budget_seconds || 0,
  };
}

/** Seasons offered as presets: no end date, or ending today or later */
export function ongoingSeasons(seasons) {
  const today = new Date(); today.setHours(0, 0, 0, 0);
  return (seasons || []).filter(s => {
    if (!s.end_date) return true;
    const end = new Date(s.end_date); end.setHours(23, 59, 59, 999);
    return end >= today;
  });
}

/** Can this pool be vetoed: Active Duty needs exactly 7 maps, Wingman 3/5/7, Competitive never */
export function vetoPoolCheck(mapMode, poolSize) {
  if (mapMode === 'competitive') return { ok: false, reason: 'competitive' };
  if (mapMode === 'active_duty') return poolSize === 7 ? { ok: true } : { ok: false, reason: 'active_duty', count: poolSize };
  return [3, 5, 7].includes(poolSize) ? { ok: true } : { ok: false, reason: 'wingman', count: poolSize };
}

/**
 * Validation (same rules as the old modal). Returns { errors, message }:
 * errors = { team1_id, team2_id, server_id, maps: [slot indexes], veto_style, wingman_pool }.
 */
export function validate(form, mapMode, poolSize) {
  const errors = {};
  if (!form.team1_id) errors.team1_id = true;
  if (!form.team2_id) errors.team2_id = true;
  const sameTeam = form.team1_id && form.team2_id && String(form.team1_id) === String(form.team2_id);
  if (sameTeam) { errors.team1_id = true; errors.team2_id = true; }
  if (!form.server_id) errors.server_id = true;
  if (!form.veto_enabled) {
    const missing = [];
    for (let i = 0; i < form.best_of; i++) if (!form.maps[i]) missing.push(i);
    if (missing.length) errors.maps = missing;
  }
  if (form.veto_enabled && !form.veto_style) errors.veto_style = true;
  if (form.veto_enabled && mapMode === 'wingman' && ![3, 5, 7].includes(poolSize)) errors.wingman_pool = true;

  let message = '';
  if (sameTeam) message = 'same_team';
  else if (errors.team1_id || errors.team2_id) message = 'teams';
  else if (errors.wingman_pool) message = 'wingman_pool';
  else if (errors.veto_style) message = 'veto_style';
  else if (errors.server_id) message = 'server';
  else if (errors.maps) message = 'maps';
  return { errors, message };
}

/** veto_format from style + best of + pool (wingman: by pool size) */
export function vetoFormat(form, mapMode, poolSize) {
  if (!form.veto_enabled || !form.veto_style) return '';
  const bo = form.best_of;
  if (mapMode === 'wingman') {
    const size = poolSize <= 3 ? 3 : poolSize <= 5 ? 5 : 7;
    return `wingman_${size}_${bo === 3 ? 'bo3' : 'bo1'}`;
  }
  if (form.veto_style === 'faceit') return bo === 5 ? 'faceit_bo5' : bo === 3 ? 'faceit_bo3' : 'faceit_bo1';
  return bo === 5 ? 'bo5' : bo === 3 ? 'bo3' : 'bo1';
}

/** The POST /api/matches body */
export function buildPayload(form, { mapMode, poolSize, seasonId }) {
  const n = form.best_of;
  const sides = (form.map_sides || []).slice(0, n);
  return {
    ...form,
    maps: form.veto_enabled ? [] : form.maps.slice(0, n),
    veto_format: vetoFormat(form, mapMode, poolSize) || undefined,
    map_sides: form.veto_enabled ? [] : (sides.length === n ? sides : Array(n).fill('team1_ct')),
    chat_prefix: form.chat_prefix || undefined,
    chat_prefix_color: form.chat_prefix_color || undefined,
    cvars_warmup: (form.cvars_warmup || []).filter(c => c.trim() !== ''),
    cvars_knife_live: (form.cvars_knife_live || []).filter(c => c.trim() !== ''),
    season_id: seasonId ? parseInt(seasonId, 10) : null,
    map_pool: mapMode,
  };
}

export const shortMap = (name) => String(name || '').replace(/^(de_|cs_|ar_|dz_|gd_)/, '');
